/**
 * User and role management interface for admins
 * Follows the unified UX pattern with stats, filtering, and modern design
 */

import { useState, useEffect, useMemo } from 'react';
import { adminApi } from '../../services/api';
import { Role, PaginatedResponse, User } from '../../types';
import PermissionChecker from './PermissionChecker';
import { FilterBar, FilterConfig } from '../shared/FilterBar';
import { Pagination } from '../shared/Pagination';
import { useAdminFilters } from './shared/hooks/useAdminFilters';
import { StatsWidget, StatItem } from '../common/StatsWidget';
import { Users, Shield, UserCheck, RefreshCw, HelpCircle } from 'lucide-react';

interface AdminUser extends User {
  roles?: string[];
  createdAt: string;
}

// Filter configuration for users
const userFilters: FilterConfig[] = [
  {
    type: 'search',
    label: 'Search',
    key: 'search',
    placeholder: 'Search email, name, or UID...',
    width: 'lg',
  },
  {
    type: 'select',
    label: 'Role',
    key: 'role',
    width: 'md',
    options: [
      { value: '', label: 'All Roles' },
      { value: 'administrator', label: 'Administrator' },
      { value: 'customer', label: 'Customer' },
      { value: 'product-manager', label: 'Product Manager' },
      { value: 'support-agent', label: 'Support Agent' },
    ],
  },
  {
    type: 'date-range',
    label: 'Registration Date',
    key: 'date',
    width: 'md',
  },
];

export default function UserManagement(): JSX.Element {
  const [response, setResponse] = useState<PaginatedResponse<AdminUser> | null>(null);
  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [editingUser, setEditingUser] = useState<AdminUser | null>(null);
  const [selectedRoles, setSelectedRoles] = useState<string[]>([]);
  const [assigning, setAssigning] = useState(false);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Use the admin filters hook with URL synchronization
  const { filters, debouncedFilters, setFilter, clearFilters } = useAdminFilters({
    page: 1,
    limit: 25,
    search: '',
    role: '',
    dateFrom: '',
    dateTo: '',
  });

  // Load data whenever debounced filters change
  useEffect(() => {
    loadData();
  }, [debouncedFilters]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Build API params from filters
      const params: any = {
        page: debouncedFilters.page,
        limit: debouncedFilters.limit,
      };

      if (debouncedFilters.search) params.search = debouncedFilters.search;
      if (debouncedFilters.role) params.role = debouncedFilters.role;
      if (debouncedFilters.dateFrom) params.dateFrom = debouncedFilters.dateFrom;
      if (debouncedFilters.dateTo) params.dateTo = debouncedFilters.dateTo;

      const [usersData, rolesData] = await Promise.all([
        adminApi.listUsers(params),
        adminApi.getRoles(),
      ]);

      setResponse(usersData as PaginatedResponse<AdminUser>);
      setRoles(rolesData.roles);
    } catch (error) {
      console.error('Failed to load data:', error);
      setResponse(null);
    } finally {
      setLoading(false);
    }
  };

  const handleEditRoles = (user: AdminUser) => {
    setEditingUser(user);
    setSelectedRoles(user.roles || []);
    setMessage(null);
  };

  const handleToggleRole = (roleId: string) => {
    if (selectedRoles.includes(roleId)) {
      setSelectedRoles(selectedRoles.filter(r => r !== roleId));
    } else {
      setSelectedRoles([...selectedRoles, roleId]);
    }
  };

  const handleAssignRoles = async () => {
    if (!editingUser) return;

    setAssigning(true);
    setMessage(null);

    try {
      await adminApi.assignUserRoles(editingUser.uid, selectedRoles);

      setMessage({
        type: 'success',
        text: 'Roles assigned successfully! User must sign out and back in for changes to take effect.',
      });

      // Reload users to show updated roles
      await loadData();

      // Close modal after 2 seconds
      setTimeout(() => {
        setEditingUser(null);
        setMessage(null);
      }, 2000);
    } catch (error: any) {
      setMessage({
        type: 'error',
        text: error.message || 'Failed to assign roles',
      });
    } finally {
      setAssigning(false);
    }
  };

  const users = response?.items || [];
  const pagination = response?.pagination;

  // Calculate stats from current data
  const stats = useMemo(() => {
    const admins = users.filter(u => u.roles?.includes('administrator')).length;
    const withRoles = users.filter(u => u.roles && u.roles.length > 0).length;

    return {
      total: pagination?.total || users.length,
      admins,
      withRoles,
      rolesCount: roles.length,
    };
  }, [users, pagination, roles]);

  // Stat items for the widget
  const statItems: StatItem[] = useMemo(() => [
    {
      id: 'total',
      label: 'Total Users',
      value: stats.total,
      icon: Users,
      variant: 'primary',
    },
    {
      id: 'admins',
      label: 'Administrators',
      value: stats.admins,
      icon: Shield,
      variant: 'warning',
    },
    {
      id: 'withRoles',
      label: 'With Roles',
      value: stats.withRoles,
      icon: UserCheck,
      variant: 'success',
    },
  ], [stats]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
            User Management
          </h2>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Manage users and assign roles and permissions
          </p>
        </div>
        <button
          onClick={loadData}
          disabled={loading}
          className="btn btn-secondary inline-flex items-center gap-2"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          Refresh
        </button>
      </div>

      {/* Stats Section */}
      <section>
        <StatsWidget stats={statItems} columns={3} loading={loading} />
      </section>

      {/* Permission Checker */}
      <PermissionChecker />

      {/* Filters */}
      <FilterBar
        filters={userFilters}
        values={filters}
        onChange={setFilter}
        onClear={clearFilters}
        resultsCount={pagination?.total}
      />

      {/* User List */}
      {loading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="card p-4 animate-pulse">
              <div className="h-5 bg-gray-200 dark:bg-gray-700 rounded w-1/3 mb-2" />
              <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-full mb-2" />
              <div className="h-3 bg-gray-200 dark:bg-gray-700 rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : users.length === 0 ? (
        <div className="card p-12 text-center">
          <p className="text-gray-600 dark:text-gray-400 text-lg">
            No users found
          </p>
        </div>
      ) : (
        <>
          <div className="space-y-3">
            {users.map((user) => (
              <div key={user.uid} className="card p-4">
                <div className="flex items-start justify-between gap-4">
                  {/* Left: User Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5 flex-wrap">
                      <h3 className="text-lg font-bold truncate">
                        {user.displayName || 'Unnamed User'}
                      </h3>
                      {user.roles?.map((role) => (
                        <span
                          key={role}
                          className={`px-2 py-0.5 text-xs font-medium rounded ${
                            role === 'administrator'
                              ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200'
                              : 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200'
                          }`}
                        >
                          {role}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-gray-600 dark:text-gray-400 truncate mb-1">
                      {user.email}
                    </p>
                    <div className="flex gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <span className="truncate">{user.uid}</span>
                      {user.permissions && user.permissions.length > 0 && (
                        <span>{user.permissions.length} permission(s)</span>
                      )}
                      <span>
                        {new Date(user.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>

                  {/* Right: Actions */}
                  <div className="flex gap-2 flex-shrink-0">
                    <button
                      onClick={() => handleEditRoles(user)}
                      className="btn btn-secondary text-xs px-3 py-1.5"
                      title="Edit Roles"
                    >
                      Edit Roles
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {pagination && pagination.totalPages > 1 && (
            <Pagination
              currentPage={pagination.page}
              totalPages={pagination.totalPages}
              totalItems={pagination.total}
              itemsPerPage={pagination.limit}
              onPageChange={(page) => setFilter('page', page)}
              onItemsPerPageChange={(limit) => setFilter('limit', limit)}
            />
          )}
        </>
      )}

      {/* Role Assignment Modal */}
      {editingUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-start mb-6">
                <div>
                  <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                    Assign Roles
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                    {editingUser.email}
                  </p>
                </div>
                <button
                  onClick={() => setEditingUser(null)}
                  className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                >
                  ✕
                </button>
              </div>

              {message && (
                <div className={`mb-4 p-4 rounded-lg ${
                  message.type === 'success'
                    ? 'bg-green-50 dark:bg-green-900/20 text-green-800 dark:text-green-200'
                    : 'bg-red-50 dark:bg-red-900/20 text-red-800 dark:text-red-200'
                }`}>
                  <p className="text-sm">{message.text}</p>
                </div>
              )}

              <div className="space-y-3 mb-6">
                {roles.map((role) => (
                  <label
                    key={role.id}
                    className="flex items-start p-4 border-2 rounded-lg cursor-pointer transition-colors hover:bg-gray-50 dark:hover:bg-gray-700 border-gray-200 dark:border-gray-700"
                  >
                    <input
                      type="checkbox"
                      checked={selectedRoles.includes(role.id)}
                      onChange={() => handleToggleRole(role.id)}
                      className="mt-1 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                    <div className="ml-3 flex-1">
                      <div className="font-medium text-gray-900 dark:text-white">
                        {role.name}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                        {role.description}
                      </div>
                      <div className="flex flex-wrap gap-1 mt-2">
                        {role.permissions.slice(0, 6).map((permission) => (
                          <span
                            key={permission}
                            className="px-2 py-0.5 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs font-mono rounded"
                          >
                            {permission}
                          </span>
                        ))}
                        {role.permissions.length > 6 && (
                          <span className="px-2 py-0.5 text-gray-500 dark:text-gray-400 text-xs">
                            +{role.permissions.length - 6} more
                          </span>
                        )}
                      </div>
                    </div>
                  </label>
                ))}
              </div>

              <div className="border-t border-gray-200 dark:border-gray-700 pt-4">
                <p className="text-sm text-amber-600 dark:text-amber-400 mb-4">
                  ⚠️ User must sign out and sign back in for role changes to take effect
                </p>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setEditingUser(null)}
                    className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleAssignRoles}
                    disabled={assigning}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {assigning ? 'Assigning...' : 'Assign Roles'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Help CTA */}
      <section className="bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-800 dark:to-gray-750 rounded-xl p-6 border border-gray-200 dark:border-gray-700">
        <div className="flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="p-3 bg-white dark:bg-gray-700 rounded-full shadow-sm">
              <HelpCircle size={24} className="text-gray-600 dark:text-gray-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                Need help with user management?
              </h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Learn about roles, permissions, and access control
              </p>
            </div>
          </div>
          <a href="/help" className="btn btn-secondary whitespace-nowrap">
            View Help Center
          </a>
        </div>
      </section>
    </div>
  );
}
